import { useTableSelectionContext } from '@/context/TableSelectionContext';
import { motion, useScroll, Variants } from 'framer-motion';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext, useLocation } from 'react-router-dom';
import { TDR } from 'tdr-common';
import { doesntConflict, useSearch } from '../../api/availability/search';
import { useTableAnnouncementsByRestaurantSuspense } from '../../api/getTableAnnouncementsByRestaurant';
import { useTimeslots } from '../../api/getTimeslots';
import { CAROUSEL_SCROLL_POSITION_KEY_DESKTOP, CAROUSEL_SCROLL_POSITION_KEY_MOBILE } from '../../common/constants';
import { GlobalParameterContext } from '../../context/new/GlobalParameterContext';
import { analytics } from '../../helpers/analytics';
import {
	CAROUSEL_RESULTS_LOADED,
	OPEN_PARAMETERS_NO_AVAILABILITY,
	PAGE_SCROLLED_TO_BOTTOM
} from '../../helpers/analytics/constants';
import { useGTMAnalytics } from '../../helpers/gtm/surface/useGTMAnalytics';
import useScrollRestoration from '../../hooks/useScrollRestoration';
import { useSessionStorage } from '../../hooks/useSessionStorage';
import { AvailabilityGuardContext, serializeSearchFiltersForURL } from '../../routes/AvailabilityGuard';
import AlternativeBookingOptions from '../alternativeBookingOption/AlternativeBookingOption';
import { emitTableClicked } from './cardAnalytics';
import { getGroupSizeConflictTables } from './categorizeTables';
import { useTableCategorization } from './hooks/useTableCategorization';
import { ClosedDateLinkNotice } from '../closed-date-link-notice/ClosedDateLinkNotice';
import { getClosedDateLinkNotice, logClosedDateLinkNoticeDebug, resolveClosedDateCtaHref } from '../../helpers/closedDateConfig';
import { useRefreshFlash } from '../../hooks/useRefreshFlash';
import { usePendingParameters } from '../../context/open-parameters/PendingParametersContext';
import { MobileOpenSearchBar } from '../open-parameters/mobile/MobileOpenSearchBar';
import { useOpenParametersEmptySearch } from '../../hooks/useOpenParametersEmptySearch';
import { ResultsSections } from '../results/ResultsSections';
import { useResolvedSearchConfig } from '../../hooks/useResolvedSearchConfig';

const SELECTED_TABLE_STORAGE_KEY = 'carousel-selected-table';

type InventoryCarouselProps = {
	/** `slotTime` is the package's start time when the card came from a service-scoped section. */
	onClick: (item: TDR.Table, slotTime?: string) => void;
};

export function InventoryCarousel({ onClick }: InventoryCarouselProps) {
	const location = useLocation();
	const { t, i18n } = useTranslation();

	useScrollRestoration(CAROUSEL_SCROLL_POSITION_KEY_MOBILE);
	const [savedLocation, setScrollLocation] = useSessionStorage(CAROUSEL_SCROLL_POSITION_KEY_DESKTOP, '');

	const { restaurant, searchFilters, tables } = useOutletContext<AvailabilityGuardContext>();
	const { refreshNonce } = usePendingParameters();
	const isRefreshing = useRefreshFlash(refreshNonce);
	const gtm = useGTMAnalytics();
	const { setSearchString } = useContext(GlobalParameterContext);

	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const tableCardContainerRef = useRef<HTMLDivElement>(null);
	const prevResultsRef = useRef<typeof results>(null);
	const noAvailKeyRef = useRef<string | null>(null);

	const { scrollY } = useScroll({ container: scrollContainerRef });
	const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
	const { data: localAnnouncements } = useTableAnnouncementsByRestaurantSuspense(restaurant.id);

	const { globalNearestTable, setFirstAvailableTable, setGlobalNearestTable } = useTableSelectionContext();
	const [savedSelectedTable, setSavedSelectedTable] = useSessionStorage(SELECTED_TABLE_STORAGE_KEY, null);

	const { results, isLoading } = useSearch({
		params: searchFilters,
		restaurantId: restaurant.id,
		space: tables
	});

	// Every live table, not the venue's single most-compatible party size. A service-scoped
	// section decides membership from these slots, so a table missing from the map disappears
	// from the results page entirely rather than falling back to CardShell's individual fetch.
	// At The Tavern the old subset was the five 1–2 guest tables, which hid every larger table
	// from the game-day sections. Widening it also removes most of those individual fetches.
	const tableIds = tables.map(t => t.id);
	const { data: bulkTimeslots } = useTimeslots({
		tableIds,
		dateStr: searchFilters.date,
		restaurantId: restaurant.id,
		options: { groupSize: searchFilters.groupSize }
	});
	const timeslotData = bulkTimeslots?.timeSlotDocMap;

	const areAnyFiltersSet = !!searchFilters.groupSize || !!searchFilters.date || !!searchFilters.time;
	const showOpenParametersEmpty = !areAnyFiltersSet;
	const closedDateLinkNotice = getClosedDateLinkNotice(restaurant, searchFilters.date);
	const closedDateLinkHref = closedDateLinkNotice
		? resolveClosedDateCtaHref(closedDateLinkNotice.cta, location.search)
		: null;
	const showClosedDateLinkNotice = !isLoading && !!closedDateLinkNotice && !!closedDateLinkHref;
	// largeGroupInbounds no longer overrides the open-params results page;
	// inbound content is configured per restaurant via settings.searchConfig.
	const showNoticeOnlyResults = showClosedDateLinkNotice;
	const showSkeleton = isLoading || isRefreshing;

	useEffect(() => {
		logClosedDateLinkNoticeDebug({
			surface: 'carousel',
			restaurant,
			dateStr: searchFilters.date,
			isLoading,
			currentSearch: location.search
		});
	}, [restaurant, searchFilters.date, isLoading, location.search]);

	const {
		emptyStateTables,
		isLoading: isEmptyStateLoading,
		isError: isEmptyStateError
	} = useOpenParametersEmptySearch({
		restaurantId: restaurant.id,
		tables,
		enabled: showOpenParametersEmpty
	});

	const openParametersEmptyTables = isEmptyStateError
		? [...tables].sort((a, b) => (a.displayOrder ?? 9999) - (b.displayOrder ?? 9999))
		: emptyStateTables;

	const searchResultTables = tables
		.filter((t) => !results?.metadata || results.metadata.groupSize.includes(t.id))
		.sort((table1, table2) => {
			const table1DoesntConflict = doesntConflict(results, table1);
			const table2DoesntConflict = doesntConflict(results, table2);
			const MAX_SORT_ORDER = 9999;

			if (table1DoesntConflict !== table2DoesntConflict) {
				return table1DoesntConflict ? -1 : 1;
			}
			const table1HasAnnouncement = !!localAnnouncements?.[table1.id];
			const table2HasAnnouncement = !!localAnnouncements?.[table2.id];

			if (table1HasAnnouncement !== table2HasAnnouncement) {
				return table1HasAnnouncement ? -1 : 1;
			}
			return (table1.displayOrder ?? MAX_SORT_ORDER) - (table2.displayOrder ?? MAX_SORT_ORDER);
		});

	const { categories: tableCategories, counts: tableCounts } = useTableCategorization(
		searchResultTables,
		results?.resultMap
	);

	const resolvedSections = useResolvedSearchConfig(restaurant, searchFilters);
	const groupSizeConflictTables = getGroupSizeConflictTables(tables, results?.metadata);

	const handleScroll = useCallback(() => {
		if (!scrollContainerRef.current) {
			return;
		}

		const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
		const isBottom = scrollTop + clientHeight >= scrollHeight - 20;

		if (isBottom && !hasScrolledToBottom) {
			setHasScrolledToBottom(true);
			analytics.track(PAGE_SCROLLED_TO_BOTTOM, {
				page: 'Inventory Carousel',
				totalResults: searchResultTables.length,
				restaurantId: restaurant.id,
				filters: {
					groupSize: searchFilters.groupSize,
					date: searchFilters.date,
					time: searchFilters.time
				}
			});
		}
	}, [hasScrolledToBottom, searchResultTables.length, restaurant.id, searchFilters]);

	useEffect(() => {
		if (!scrollContainerRef.current || isLoading) {
			return;
		}

		const container = scrollContainerRef.current;
		container.addEventListener('scroll', handleScroll);

		return () => {
			container.removeEventListener('scroll', handleScroll);
		};
	}, [handleScroll, isLoading]);

	useEffect(() => {
		setHasScrolledToBottom(false);
	}, [searchFilters, results]);

	useEffect(() => {
		if (!isLoading && results && searchResultTables.length > 0) {
			const currentResultIds = new Set(Object.keys(results.resultMap || {}));
			const prevResultIds = prevResultsRef.current
				? new Set(Object.keys(prevResultsRef.current.resultMap || {}))
				: new Set();
			const hasResultsChanged = currentResultIds.size !== prevResultIds.size ||
				![...currentResultIds].every(id => prevResultIds.has(id));

			if (hasResultsChanged) {
				const firstAvailable = searchResultTables.find(table => !results?.resultMap?.[table.id]?.['conflict']);
				if (firstAvailable) {
					setFirstAvailableTable(firstAvailable);
				}

				const announcementsShown = localAnnouncements ?
					searchResultTables.filter(table => !!localAnnouncements[table.id]).length : 0;

				const { available: availableTables, dateConflict: dateConflictTables, timeConflict: timeConflictTables, dateSoldOut: dateSoldOutTablesCount, dateClosed: dateClosedTablesCount } = tableCounts;

				analytics.track(CAROUSEL_RESULTS_LOADED, {
					totalResults: searchResultTables.length,
					announcementsShown,
					availableTables,
					dateConflictTables,
					timeConflictTables,
					dateSoldOutTables: dateSoldOutTablesCount,
					dateClosedTables: dateClosedTablesCount,
					restaurantId: restaurant.id,
					filters: {
						groupSize: searchFilters.groupSize,
						date: searchFilters.date,
						time: searchFilters.time
					}
				});

				gtm.viewItemList({
					items: searchResultTables.map((table) => ({
						id: table.id,
						name: table.name,
						category: 'Reservation'
					}))
				});

				prevResultsRef.current = results;
			}
		}
	}, [isLoading, results, searchResultTables, localAnnouncements, restaurant.id, searchFilters, tableCounts]);

	useEffect(() => {
		if (!areAnyFiltersSet || isLoading || !results) {
			return;
		}
		if (tableCounts.available > 0) {
			noAvailKeyRef.current = null;
			return;
		}
		const key = serializeSearchFiltersForURL(searchFilters).toString();
		if (noAvailKeyRef.current === key) {
			return;
		}
		noAvailKeyRef.current = key;
		analytics.track(OPEN_PARAMETERS_NO_AVAILABILITY, {
			restaurantId: restaurant.id,
			filters: {
				groupSize: searchFilters.groupSize,
				date: searchFilters.date,
				time: searchFilters.time
			}
		});
	}, [areAnyFiltersSet, isLoading, results, tableCounts.available, searchFilters, restaurant.id]);

	useEffect(() => {
		setSearchString(serializeSearchFiltersForURL(searchFilters).toString());
	}, [searchFilters, setSearchString]);

	useEffect(() => {
		let timeoutId: NodeJS.Timeout;

		const handleScrollChange = (value: number) => {
			clearTimeout(timeoutId);
			timeoutId = setTimeout(() => {
				setScrollLocation(`${value}`);
			}, 1000);
		};

		const unsubscribe = scrollY.on('change', handleScrollChange);

		return () => {
			clearTimeout(timeoutId);
			unsubscribe();
		};
	}, [scrollY]);

	useEffect(() => {
		if (isLoading) {
			return;
		}
		if (savedLocation && scrollContainerRef.current) {
			scrollContainerRef.current.scrollTo({ top: parseInt(savedLocation, 10), behavior: 'auto' });
		}
	}, [savedLocation, isLoading]);

	useEffect(() => {
		return () => {
			setScrollLocation(`${scrollY.get()}`);
		};
	}, []);

	useEffect(() => {
		if (!isLoading && savedSelectedTable && searchResultTables?.length > 0) {
			const savedTable = searchResultTables.find(table => table.id === savedSelectedTable.id);
			if (savedTable) {
				setGlobalNearestTable(savedTable);
				setTimeout(() => {
					setSavedSelectedTable(null);
				}, 2000);
			}
			else {
				setSavedSelectedTable(null);
			}
		}
	}, [savedSelectedTable, isLoading, searchResultTables, setGlobalNearestTable, setSavedSelectedTable]);

	const handleTableClick = useCallback((table: TDR.Table, index: number, slotTime?: string) => {
		setSavedSelectedTable(table);

		emitTableClicked({
			table,
			restaurantId: restaurant.id,
			searchFilters,
			counts: tableCounts,
			totalResults: searchResultTables.length,
			position: index,
			tableStatus: 'available',
			announcement: localAnnouncements?.[table.id],
			hasScrolledToBottom
		});

		onClick(table, slotTime);
	}, [setSavedSelectedTable, localAnnouncements, tableCounts, searchResultTables.length, searchFilters, hasScrolledToBottom, restaurant.id, onClick]);

	const headerVariants: Variants = {
		visible: {
			opacity: 1,
			y: 0
		},
		hidden: {
			opacity: 0,
			y: '-100%'
		}
	};

	return (
		<div className="relative h-full sm:w-[400px] xl:w-[800px]">
			<div className="flex flex-col h-full bg-tablz-dark-gray-1" data-cy='table-carousel'>
				<motion.div
					initial={'hidden'}
					animate={'visible'}
					exit={'hidden'}
					variants={headerVariants}
					transition={{ duration: 0.4 }}
					className='sticky top-0 sm:hidden z-10'
				>
					<div className='bg-tablz-dark-gray-2 px-4 py-3'>
						<MobileOpenSearchBar />
					</div>
				</motion.div>
				<motion.div
					className="flex flex-col margin-x-auto relative bg-tablz-dark-gray-1 overflow-y-auto sm:pt-8 sm:pb-3 sm:px-5 overflow-x-hidden h-full"
					ref={scrollContainerRef}
					initial={'hidden'}
					animate={'visible'}
					exit={'hidden'}
					transition={{ duration: 0.4 }}
				>
					<motion.div
						key={showSkeleton ? 'loading' : 'loaded'}
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ duration: 0.12 }}
						className="flex flex-col flex-1"
					>
						{showNoticeOnlyResults ? (
							<section className='px-5 pt-6 sm:px-0 sm:pt-4'>
								<ClosedDateLinkNotice restaurant={restaurant} config={closedDateLinkNotice} />
							</section>
						) : (
							<>
						{restaurant?.settings?.alternativeBookingOption && (
							<div className="px-5 sm:px-0">
								<AlternativeBookingOptions
									label={restaurant.settings.alternativeBookingOption.label}
									selfLabel={restaurant.settings.alternativeBookingOption.selfLabel}
									href={
										restaurant.settings.alternativeBookingOption.href.replace(/\/$/, '') +
										(areAnyFiltersSet ? '?' + serializeSearchFiltersForURL(searchFilters).toString() : '')
									}
									selfPosition={restaurant.settings.alternativeBookingOption.selfPosition}
								/>
							</div>
						)}
						<ResultsSections
							sections={resolvedSections}
							restaurant={restaurant}
							searchFilters={searchFilters}
							inventory={{
								categories: { ...tableCategories, groupSizeConflict: groupSizeConflictTables },
								counts: tableCounts,
								totalResults: searchResultTables.length,
								results,
								announcements: localAnnouncements,
								viewedTableId: globalNearestTable?.id,
								timeslotDocMap: timeslotData,
								hasScrolledToBottom,
								showSkeleton,
								emptyState: {
									show: showOpenParametersEmpty,
									isLoading: isEmptyStateLoading || isRefreshing,
									tables: openParametersEmptyTables
								},
								onTableClick: handleTableClick,
								cardContainerRef: tableCardContainerRef
							}}
						/>
							</>
						)}
						{!showNoticeOnlyResults ? (
						<div className="sm:hidden bg-tablz-dark-gray-2 border-t border-tablz-med-gray-4 py-5 px-4 flex justify-between items-center">
							<span className="text-tablz-text-gray-2 font-poppins text-sm font-normal">{t('actions.change_language')}</span>
							<select
								aria-label="Language"
								className="bg-zinc-800 text-white border border-zinc-700 rounded-md px-2 py-1"
								value={i18n.language?.startsWith('fr') ? 'fr' : 'en'}
								onChange={(e) => i18n.changeLanguage(e.target.value)}
							>
								{(restaurant?.supportedLocales || ['en']).map((locale) => (
									<option key={locale} value={locale}>
										{locale === 'en' ? 'English' : locale === 'fr' ? 'Français' : locale}
									</option>
								))}
							</select>
						</div>
						) : null}
					</motion.div>
				</motion.div>
			</div>
		</div>
	);
}
